/* eslint-disable */
import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { RichTextProvider } from 'reactjs-tiptap-editor';
import { EditorContent, useEditor } from '@tiptap/react';
// Base Kit - now using individual TipTap extensions
import { Document } from '@tiptap/extension-document';
import { Text } from '@tiptap/extension-text';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Dropcursor, Gapcursor, Placeholder, TrailingNode } from '@tiptap/extensions';
import { HardBreak } from '@tiptap/extension-hard-break';
import { TextStyle } from '@tiptap/extension-text-style';
import { ListItem } from '@tiptap/extension-list';

import { History, RichTextUndo, RichTextRedo } from 'reactjs-tiptap-editor/history';
import { SearchAndReplace, RichTextSearchAndReplace } from 'reactjs-tiptap-editor/searchandreplace';
import { FormatPainter, RichTextFormatPainter } from 'reactjs-tiptap-editor/formatpainter';

import { Clear, RichTextClear } from 'reactjs-tiptap-editor/clear';
import { FontFamily, RichTextFontFamily } from 'reactjs-tiptap-editor/fontfamily';
import { Heading, RichTextHeading } from 'reactjs-tiptap-editor/heading';
import { FontSize, RichTextFontSize } from 'reactjs-tiptap-editor/fontsize';
import { Bold, RichTextBold } from 'reactjs-tiptap-editor/bold';
import { Italic, RichTextItalic } from 'reactjs-tiptap-editor/italic';
import { TextUnderline, RichTextUnderline } from 'reactjs-tiptap-editor/textunderline';
import { Strike, RichTextStrike } from 'reactjs-tiptap-editor/strike';

import { Color, RichTextColor } from 'reactjs-tiptap-editor/color';
import { Highlight, RichTextHighlight } from 'reactjs-tiptap-editor/highlight';

import { BulletList, RichTextBulletList } from 'reactjs-tiptap-editor/bulletlist';
import { OrderedList, RichTextOrderedList } from 'reactjs-tiptap-editor/orderedlist';
import { TaskList, RichTextTaskList } from 'reactjs-tiptap-editor/tasklist';

import { TextAlign, RichTextAlign } from 'reactjs-tiptap-editor/textalign';
import { Indent, RichTextIndent } from 'reactjs-tiptap-editor/indent';
import { LineHeight, RichTextLineHeight } from 'reactjs-tiptap-editor/lineheight';
import { Link, RichTextLink } from 'reactjs-tiptap-editor/link';

import { Blockquote, RichTextBlockquote } from 'reactjs-tiptap-editor/blockquote';
import { HorizontalRule, RichTextHorizontalRule } from 'reactjs-tiptap-editor/horizontalrule';

import { Column, ColumnNode, MultipleColumnNode, RichTextColumn } from 'reactjs-tiptap-editor/column';
import { Table, RichTextTable } from 'reactjs-tiptap-editor/table';

import { ExportPdf, RichTextExportPdf } from 'reactjs-tiptap-editor/exportpdf';
import { ExportWord, RichTextExportWord } from 'reactjs-tiptap-editor/exportword';

import { TextDirection, RichTextTextDirection } from 'reactjs-tiptap-editor/textdirection';

const DocumentColumn = Document.extend({
  content: '(block|columns)+',
});

const BaseKit = [
  DocumentColumn,
  Text,
  Dropcursor.configure({
    class: 'reactjs-tiptap-editor-theme',
    color: 'hsl(var(--primary))',
    width: 2,
  }),
  Gapcursor,
  HardBreak,
  Paragraph,
  TrailingNode,
  ListItem,
  TextStyle,
  History,
  SearchAndReplace,
  Clear,
  FontFamily,
  Heading,
  FontSize,
  Bold,
  Italic,
  TextUnderline,
  Strike,
  Color,
  Highlight,
  BulletList,
  OrderedList,
  TaskList,
  TextAlign,
  Indent,
  LineHeight,
  Blockquote,
  HorizontalRule,
  Column,
  ColumnNode,
  MultipleColumnNode,
  Table,
  ExportPdf,
  ExportWord,
  TextDirection
];

const extensions = [...BaseKit];

const TextEditor = (props) => {
  const editorRef = useRef(null);
  const lastLoadedId = useRef(null);
  // Import CSS Doesn't work with our setup: import 'reactjs-tiptap-editor/style.css';
  const content = props.companyNotes?.notes
  const editor = useEditor({
    content,
    extensions,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (props.page === 'practice' || props.page === 'firm') {
        props.onChangeNotes(props.companyNotes, props.tabInfo?.can_edit)(html)
      } else {
        props.onChangeNotes(props.companyNotes)(html)
      }
    },
  });

  // // Toggle editable state
  // editor.setEditable(true)  // or false

  // // Check editable state
  // const isEditable = editor.isEditable

  if (props.page === 'practice' || props.page === 'firm') {
    return (
      <div className='text-editor-pdf' style={{ 'position': 'relative', 'maxWidth': '812px', 'minHeight': '1054px', 'margin': '10px auto' }}>
        {(!props.tabInfo?.can_edit) && (
          <div style={{
            position: 'absolute',
            top: 0, left: 0,
            width: '100%', height: '100%',
            zIndex: 10,
            cursor: 'not-allowed'
          }} />
        )}
        <RichTextProvider editor={editor}>
          <div className='editor-container richtext-rounded-[0.5rem] richtext-bg-background richtext-shadow richtext-overflow-hidden richtext-outline richtext-outline-1'>
            <div className='richtext-flex richtext-flex-col richtext-w-full richtext-max-h-full'>
              <div className='richtext-px-1 richtext-py-2 !richtext-border-b' style={{ 'pointerEvents': 'auto', 'opacity': 1, 'borderColor': '#e4e4e7' }}>
                <div className='editor-toolbar richtext-relative richtext-flex richtext-flex-wrap richtext-h-auto richtext-gap-y-1 richtext-gap-x-1'>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextUndo />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextRedo />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextSearchAndReplace />
                  </div>
                  
                  <div className='richtext-flex richtext-items-center'>
                    <div data-orientation="vertical" role="none" className="richtext-shrink-0 richtext-bg-border richtext-h-full richtext-w-[1px] !richtext-h-[16px] !richtext-mx-[10px]"></div>
                    <RichTextFormatPainter />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextClear />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextFontFamily />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <div data-orientation="vertical" role="none" className="richtext-shrink-0 richtext-bg-border richtext-h-full richtext-w-[1px] !richtext-h-[16px] !richtext-mx-[10px]"></div>
                    <RichTextHeading />
                  </div>

                  <div className='richtext-flex richtext-items-center'>
                    <RichTextFontSize />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextBold />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextItalic />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextUnderline />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextStrike /> 
                  </div>

                  <div className='richtext-flex richtext-items-center'>
                    <div data-orientation="vertical" role="none" className="richtext-shrink-0 richtext-bg-border richtext-h-full richtext-w-[1px] !richtext-h-[16px] !richtext-mx-[10px]"></div>
                    <RichTextColor />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextHighlight />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextBulletList />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextOrderedList />
                  </div>


                  <div className='richtext-flex richtext-items-center'>
                    <div data-orientation="vertical" role="none" className="richtext-shrink-0 richtext-bg-border richtext-h-full richtext-w-[1px] !richtext-h-[16px] !richtext-mx-[10px]"></div>
                    <RichTextAlign/>
                  </div>
                    <div className='richtext-flex richtext-items-center'>
                    <RichTextIndent />
                    </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextLineHeight />
                  </div>

                  <div className='richtext-flex richtext-items-center'>
                    <div data-orientation="vertical" role="none" className="richtext-shrink-0 richtext-bg-border richtext-h-full richtext-w-[1px] !richtext-h-[16px] !richtext-mx-[10px]"></div>
                    <RichTextTaskList />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextBlockquote />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextLink />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextHorizontalRule />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextColumn />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextTable />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextExportPdf />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextExportWord />
                  </div>  
                  {/* Add more toolbar buttons */}
                </div>
              </div>
              

              {/* Editor Content */}
              <div className='richtext-relative text-editor-content' aria-expanded='false' >
                <EditorContent editor={editor} />
              </div>

              {/* Bubble Menus */}
              {/* <RichTextBubbleText /> */}
            </div>
          </div>
        </RichTextProvider>
      </div>
    )
  } else {
    return (
      <div className='text-editor-pdf' style={{ 'maxWidth': '812px', 'minHeight': '1054px', 'margin': '10px auto' }}>
        <RichTextProvider editor={editor}>
          <div className='editor-container richtext-rounded-[0.5rem] richtext-bg-background richtext-shadow richtext-overflow-hidden richtext-outline richtext-outline-1'>
            <div className='richtext-flex richtext-flex-col richtext-w-full richtext-max-h-full'>

              <div className='richtext-px-1 richtext-py-2 !richtext-border-b' style={{ 'pointerEvents': 'auto', 'opacity': 1, 'borderColor': '#e4e4e7' }}>
                <div className='editor-toolbar richtext-relative richtext-flex richtext-flex-wrap richtext-h-auto richtext-gap-y-1 richtext-gap-x-1'>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextUndo />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextRedo />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextSearchAndReplace />
                  </div>
                  
                  <div className='richtext-flex richtext-items-center'>
                    <div data-orientation="vertical" role="none" className="richtext-shrink-0 richtext-bg-border richtext-h-full richtext-w-[1px] !richtext-h-[16px] !richtext-mx-[10px]"></div>
                    <RichTextFormatPainter />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextClear />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextFontFamily />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <div data-orientation="vertical" role="none" className="richtext-shrink-0 richtext-bg-border richtext-h-full richtext-w-[1px] !richtext-h-[16px] !richtext-mx-[10px]"></div>
                    <RichTextHeading />
                  </div>

                  <div className='richtext-flex richtext-items-center'>
                    <RichTextFontSize />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextBold />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextItalic />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextUnderline />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextStrike /> 
                  </div>

                  <div className='richtext-flex richtext-items-center'>
                    <div data-orientation="vertical" role="none" className="richtext-shrink-0 richtext-bg-border richtext-h-full richtext-w-[1px] !richtext-h-[16px] !richtext-mx-[10px]"></div>
                    <RichTextColor />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextHighlight />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextBulletList />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextOrderedList />
                  </div>


                  <div className='richtext-flex richtext-items-center'>
                    <div data-orientation="vertical" role="none" className="richtext-shrink-0 richtext-bg-border richtext-h-full richtext-w-[1px] !richtext-h-[16px] !richtext-mx-[10px]"></div>
                    <RichTextAlign/>
                  </div>
                    <div className='richtext-flex richtext-items-center'>
                    <RichTextIndent />
                    </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextLineHeight />
                  </div>

                  <div className='richtext-flex richtext-items-center'>
                    <div data-orientation="vertical" role="none" className="richtext-shrink-0 richtext-bg-border richtext-h-full richtext-w-[1px] !richtext-h-[16px] !richtext-mx-[10px]"></div>
                    <RichTextTaskList />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextBlockquote />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextLink />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextHorizontalRule />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextColumn />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextTable />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextExportPdf />
                  </div>
                  <div className='richtext-flex richtext-items-center'>
                    <RichTextExportWord />
                  </div>  
                  {/* Add more toolbar buttons */}
                </div>
              </div>
              

              {/* Editor Content */}
              <div className='richtext-relative text-editor-content' aria-expanded='false' >
                <EditorContent editor={editor} />
              </div>

              {/* Bubble Menus */}
              {/* <RichTextBubbleText /> */}
            </div>
          </div>
        </RichTextProvider>
      </div>
    );
  };
}


export default TextEditor;
